import React from "react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, transformBlogPost } from "../services/api";
import StickyHeadline from "./StickyHeadline";
import { Headline, Paragraph, Section, ContentBlock } from "./ui";

export function ProjectsGrid({
  postSlugs = null,
  numberOfPosts = 3,
  showHeader = true,
  showIntro = true,
  header = "WTM Werkstatt Journal",
  darkMode = false,
  introText = "Von Modellvorstellungen über Technik-Einblicke bis zur Kaufberatung – unser Blog liefert fundiertes Porsche-Wissen.",
  margin = false,
  renderStickyHeadline = false,
  stickyHeadline = null,
  navbarHeight = 70,
}) {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Helper function to handle navigation to blog posts
  const navigateToBlogPost = (post) => {
    navigate(`/blog/${post.slug || post.id}`);
  };

  // Mobile carousel scroll handler
  const handleScroll = (e) => {
    const container = e.target;
    const slideWidth = container.scrollWidth / posts.length;
    const newSlide = Math.round(container.scrollLeft / slideWidth);
    setCurrentSlide(newSlide);
  };

  // Scroll to specific slide
  const scrollToSlide = (index) => {
    const container = document.querySelector(".mobile-carousel");
    if (container) {
      const slideWidth = container.scrollWidth / posts.length;
      container.scrollTo({
        left: slideWidth * index,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        let blogPostsData;

        if (postSlugs && Array.isArray(postSlugs) && postSlugs.length > 0) {
          // Fetch specific blog posts by slugs
          blogPostsData = await api.getBlogPostsBySlugs(postSlugs);
        } else {
          // Fetch random blog posts (get more than needed and slice)
          const response = await api.getBlogPosts(1, numberOfPosts * 2);
          blogPostsData = response.data || [];
        }

        const transformedPosts = blogPostsData
          .map(transformBlogPost)
          .filter(Boolean)
          .slice(0, numberOfPosts); // Limit to requested number

        setPosts(transformedPosts);
      } catch (err) {
        console.error("Error fetching blog posts:", err);
        setError("Failed to load blog posts");
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [postSlugs, numberOfPosts]);

  if (error) {
    return (
      <Section variant="primary" spacing="comfortable" margin={margin}>
        <ContentBlock>
          <div className="text-center">
            <Paragraph variant="lead" color="secondary">
              {error}
            </Paragraph>
          </div>
        </ContentBlock>
      </Section>
    );
  }

  if (loading) {
    return (
      <Section variant="primary" spacing="comfortable" margin={margin}>
        <ContentBlock>
          <div className="text-center">
            <Paragraph variant="lead" color="secondary">
              Lade Artikel...
            </Paragraph>
          </div>
        </ContentBlock>
      </Section>
    );
  }

  if (posts.length === 0) {
    return (
      <Section variant="primary" spacing="comfortable" margin={margin}>
        <ContentBlock>
          <div className="text-center">
            <Paragraph variant="lead" color="secondary">
              Keine Artikel gefunden.
            </Paragraph>
          </div>
        </ContentBlock>
      </Section>
    );
  }

  return (
    <div className="relative">
      {/* Sticky Headline - Full Width */}
      {renderStickyHeadline && stickyHeadline && (
        <StickyHeadline headline={stickyHeadline} navbarHeight={navbarHeight} />
      )}

      <Section
        id="projects"
        variant="primary"
        spacing="spacious"
        margin={margin}
      >
        <ContentBlock maxWidth="none" spacing="comfortable" className="mx-auto">
          {showHeader && (
            <ContentBlock maxWidth="7xl" spacing="none" className="sm:px-8">
              <Headline
                variant="splitscreen"
                color="secondary"
                className="text-left mb-0"
              >
                {header}
              </Headline>
            </ContentBlock>
          )}

          {/* Desktop Grid */}
          <div className="hidden md:block">
            <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 sm:gap-4">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="rounded-sm shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group h-full flex flex-col"
                  style={{ backgroundColor: "var(--color-primary-contrast)" }}
                  onClick={() => navigateToBlogPost(post)}
                >
                  <div className="relative h-96 overflow-hidden">
                    <img
                      src={post.heroImage}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all duration-300" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <Headline
                      variant="generic-card"
                      color="secondary"
                      className="text-left mb-3"
                    >
                      {post.title}
                    </Headline>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Carousel */}
          <div className="block md:hidden">
            <div
              className="mobile-carousel flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 pb-4"
              onScroll={handleScroll}
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="flex-shrink-0 w-full snap-center rounded-sm shadow-lg overflow-hidden cursor-pointer group"
                  style={{ backgroundColor: "var(--color-primary-contrast)" }}
                  onClick={() => navigateToBlogPost(post)}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={post.heroImage}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4">
                    <Headline
                      variant="generic-card"
                      color="secondary"
                      className="text-left mb-2"
                    >
                      {post.title}
                    </Headline>
                  </div>
                </div>
              ))}
            </div>

            {/* Dots Indicator */}
            {posts.length > 1 && (
              <div className="flex justify-center mt-4 space-x-2">
                {posts.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => scrollToSlide(index)}
                    className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                      index === currentSlide
                        ? "bg-gray-300"
                        : "bg-gray-600 hover:bg-gray-400"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </ContentBlock>
      </Section>
    </div>
  );
}

export default ProjectsGrid;
